import React, { useState } from 'react';
import { BookOpenIcon, BusIcon, CheckIcon, HandIcon, HomeIcon, PlaneIcon, SchoolIcon, ShipIcon, SunIcon, UtensilsIcon } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { levels } from '../data/content';
import { SectionHeading } from './SectionHeading';

/** One simple picture per story line (Week 01 · My School Day) */
const storyIcons: LucideIcon[] = [SchoolIcon, BusIcon, HandIcon, BookOpenIcon, UtensilsIcon, SunIcon, HomeIcon];
type Choice = {
  word: string;
  Icon: LucideIcon;
  correct?: boolean;
};
const bus: Choice = {
  word: 'bus',
  Icon: BusIcon,
  correct: true
};
const boat: Choice = {
  word: 'boat',
  Icon: ShipIcon
};
const plane: Choice = {
  word: 'plane',
  Icon: PlaneIcon
};

/** A big picture card, like the ones in the book. The correct answer gets a mint ring and a check. */
function PictureCard({
  c,
  big
}: {
  c: Choice;
  big?: boolean;
}) {
  return <div className={`relative flex flex-col items-center justify-center gap-2 rounded-2xl border-2 bg-white ${big ? 'min-h-[150px] p-5' : 'min-h-[118px] p-3'} ${c.correct ? 'border-leaf ring-4 ring-leaf/20' : 'border-hair'}`} data-id="element-112" data-mp="3v2nfscsptowb">
      {c.correct && <span className="absolute right-2 top-2 flex h-6 w-6 items-center justify-center rounded-full bg-leaf text-white" data-id="element-113" data-mp="1j9zexrs1hyav">
          <CheckIcon className="h-3.5 w-3.5" strokeWidth={3} aria-hidden="true" data-id="element-114" data-mp="1o9u1jj4s81q4" />
        </span>}
      <c.Icon className={`${big ? 'h-14 w-14' : 'h-10 w-10'} text-brand`} strokeWidth={1.75} aria-hidden="true" data-id="element-115" data-mp="1s9cpyovtauxp" />
      <span className={`font-display font-extrabold text-ink ${big ? 'text-[20px]' : 'text-[16px]'}`} data-id="element-116" data-mp="1x7w9yjquhwnf">{c.word}</span>
    </div>;
}

/** New in this landing: the same Week 01 question shown at Level 1, 2 and 3 */
export function LevelsSection() {
  const [active, setActive] = useState(0);
  const tab = levels.tabs[active];
  return <section aria-labelledby="levels-title" className="bg-sun-soft py-12 md:py-20" data-id="element-117" data-mp="2fzyit8x8w8n4">
      <div className="mx-auto max-w-page px-5 md:px-8" data-id="element-118" data-mp="2fxvxh271f3oq">
        <SectionHeading id="levels-title" eyebrow={levels.eyebrow} title={levels.title} sub={levels.sub} data-id="element-119" data-mp="gw9um02gqelk" />

        <div className="mx-auto mt-8 grid max-w-[980px] gap-5 md:mt-12 lg:grid-cols-[0.9fr_1.1fr] lg:items-start" data-id="element-120" data-mp="bxf226ep1bo9">
          {/* The adapted story, one line per sentence with a picture */}
          <div className="overflow-hidden rounded-2xl bg-white shadow-[0_18px_40px_-24px_rgba(15,27,61,0.45)]" data-id="element-121" data-mp="kvr20z1g9ltd">
            <div className="bg-brand px-5 py-3" data-id="element-122" data-mp="zkeyd7j4dzkd">
              <p className="font-display text-[12px] font-extrabold tracking-[0.12em] text-sun" data-id="element-123" data-mp="umvhs0yy9ap9">{levels.storyTitle}</p>
            </div>
            <ol className="divide-y divide-hair" data-id="element-124" data-mp="18ld1lnlqi31w">
              {levels.story.map((line, i) => {
              const Icon = storyIcons[i];
              return <li key={line} className="flex items-center gap-3 px-5 py-2.5" data-id="element-125" data-mp="3adhscvrhyub1" data-mpi={i}>
                    <span className="w-5 shrink-0 font-display text-[14px] font-black text-coral-ink" data-id="element-126" data-mp="36c5ww9lxzeux">{i + 1}</span>
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-mist text-brand" data-id="element-127" data-mp="2w7ph1g8gpxr4">
                      <Icon className="h-5 w-5" strokeWidth={2} aria-hidden="true" data-id="element-128" data-mp="308ri82lxwjvq" />
                    </span>
                    <span className="text-[17px] font-semibold leading-snug text-ink" data-id="element-129" data-mp="2mhmplkgzoruw">{line}</span>
                  </li>;
            })}
            </ol>
          </div>

          {/* The same question at 3 levels */}
          <div data-id="element-130" data-mp="2cmcl8q34c8dy">
            <div role="tablist" aria-label="Support levels" className="grid grid-cols-3 gap-2" data-id="element-131" data-mp="2hjbhpfa98rfb">
              {levels.tabs.map((t, i) => {
              const on = i === active;
              return <button key={t.level} role="tab" type="button" aria-selected={on} aria-controls="levels-panel" onClick={() => setActive(i)} className={`flex min-h-[64px] flex-col items-center justify-center rounded-xl border-2 px-1.5 py-2 text-center transition-colors duration-150 ease-out focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand ${on ? 'border-brand bg-brand text-white' : 'border-transparent bg-white text-ink hover:border-brand/30'}`} data-id="element-132" data-mp="28id8mikyltpv" data-mpi={i}>
                    <span className={`font-display text-[13px] font-black tracking-[0.06em] ${on ? 'text-sun' : 'text-coral-ink'}`} data-id="element-133" data-mp="1jw2b78seuvcw">{t.level}</span>
                    <span className="mt-0.5 font-display text-[11px] font-extrabold leading-tight tracking-[0.02em] sm:text-[12px]" data-id="element-134" data-mp="1f3iyee1js495">{t.name}</span>
                  </button>;
            })}
            </div>

            <div id="levels-panel" role="tabpanel" className="mt-3 rounded-2xl bg-white p-5 md:p-6" data-id="element-135" data-mp="1152usvyzetv8">
              <p className="font-display text-[12px] font-extrabold tracking-[0.12em] text-ink/55" data-id="element-136" data-mp="w5zn13rma9jk">QUESTION 2</p>
              <p className="mt-1 font-display text-[22px] font-black leading-tight text-brand md:text-[24px]" data-id="element-137" data-mp="37tusb0vp7rpi">{levels.question}</p>

              <div className="mt-4" data-id="element-138" data-mp="3cr3c15whw3sv">
                {active === 0 && <div className="grid grid-cols-2 gap-3" data-id="element-139" data-mp="3gthsjjcoyrgz">
                    <PictureCard c={bus} big data-id="element-140" data-mp="3gs6rvsejlzu3" />
                    <PictureCard c={boat} big data-id="element-141" data-mp="3ltcj1lsk1f1i" />
                  </div>}
                {active === 1 && <div data-id="element-142" data-mp="2p4es6enxe19m">
                    <div className="grid grid-cols-3 gap-2.5" data-id="element-143" data-mp="2p10n2ynvndyh">
                      <PictureCard c={boat} data-id="element-144" data-mp="2t58gh95a5i31" />
                      <PictureCard c={bus} data-id="element-145" data-mp="2y3i9pe7g28m0" />
                      <PictureCard c={plane} data-id="element-146" data-mp="2y2gvj0v4t602" />
                    </div>
                    <p className="mt-3 inline-block rounded-lg bg-sun-soft px-3 py-1.5 font-display text-[15px] font-extrabold text-ink" data-id="element-147" data-mp="32urxsadch3vs">Say it: “bus”</p>
                  </div>}
                {active === 2 && <div data-id="element-148" data-mp="2acmmkudxo1l3">
                    <p className="flex flex-wrap items-end gap-x-2 gap-y-1 text-[20px] font-semibold leading-relaxed text-ink" data-id="element-149" data-mp="2ac4f11ltm0xv">
                      {levels.sentence}
                      <span className="inline-block min-w-[96px] border-b-[3px] border-brand text-center font-display font-black text-leaf-ink" data-id="element-150" data-mp="38op3qvgbwshf">bus</span>
                      {levels.sentenceEnd}
                    </p>
                    <div className="mt-4 flex flex-wrap items-center gap-2" data-id="element-151" data-mp="3igd45luv5jv1">
                      <span className="text-[13px] font-bold tracking-[0.08em] text-ink/55" data-id="element-152" data-mp="2ur0ny214cbj8">WORD BANK</span>
                      {levels.wordBank.map(w => <span key={w} className="rounded-lg border border-hair bg-mist px-3 py-1 font-display text-[15px] font-bold text-ink" data-id="element-153" data-mp="33qfbaslzo5zi">
                          {w}
                        </span>)}
                    </div>
                  </div>}
              </div>

              <p className="mt-5 rounded-xl bg-mist px-4 py-3 text-[15px] leading-relaxed text-ink/80" data-id="element-154" data-mp="e1v4oo5cblak">
                <span className="font-display font-extrabold text-brand" data-id="element-155" data-mp="mi4638riedk7">{tab.level} · {tab.name}: </span>
                {tab.text}
              </p>
            </div>
            <p className="mt-3 text-center text-[14px] font-medium text-ink/65 lg:text-left" data-id="element-156" data-mp="ck67brc0wydc">{levels.footnote}</p>
          </div>
        </div>
      </div>
    </section>;
}